import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Plus, Trash2, Check, Pencil, Loader2 } from 'lucide-react';
import { apiClient } from '../../lib/apiClient';

/**
 * Slide-out drawer for managing the showcase filter axes.
 *
 * Industries and types are stored values, not hardcoded lists, so adding a
 * sector is a form entry rather than a code change. This is the screen that
 * drives the CRUD the API already exposes.
 *
 * Opens from the right over the admin page rather than navigating away, because
 * filters are edited WHILE tagging projects — making it a separate page would
 * mean losing the project you were part-way through editing.
 */

const AXES = [
  { kind: 'industry', label: 'Industries', hint: 'Finance, Construction, Healthcare…' },
  { kind: 'project_type', label: 'Types', hint: 'CRM, Project Manager, Full Stack…' },
];

const FilterDrawer = ({ open, onClose, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drafts, setDrafts] = useState({ industry: '', project_type: '' });
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [busy, setBusy] = useState(false);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/showcase/filters');
      setRows(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (err) {
      setError('Could not load filters.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Escape closes, and focus moves into the panel so keyboard users are not
  // left behind on the page underneath.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    panelRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const notify = () => onChanged?.();

  const add = async (kind) => {
    const name = drafts[kind].trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.post('/showcase/filters', { kind, name });
      setDrafts((d) => ({ ...d, [kind]: '' }));
      await load();
      notify();
    } catch (err) {
      // 409 is the duplicate guard — worth saying plainly rather than "failed".
      setError(
        err?.response?.status === 409
          ? `"${name}" already exists.`
          : err?.response?.data?.detail || 'Could not add that.',
      );
    } finally {
      setBusy(false);
    }
  };

  const rename = async (row) => {
    const name = editValue.trim();
    if (!name || name === row.name) {
      setEditingId(null);
      return;
    }
    setBusy(true);
    try {
      await apiClient.put(`/showcase/filters/${row.id}`, { name });
      setEditingId(null);
      await load();
      notify();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not rename that.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (
      !window.confirm(
        `Delete "${row.name}"? It will be removed from any project using it.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const res = await apiClient.delete(`/showcase/filters/${row.id}`);
      const cleared = res?.data?.projects_cleared || 0;
      // Say what it actually touched — a silent delete hides the blast radius.
      if (cleared) setError(`Removed, and cleared from ${cleared} project${cleared === 1 ? '' : 's'}.`);
      await load();
      notify();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not delete that.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Manage filters"
        data-testid="filter-drawer"
        className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col outline-none"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-gray-900 font-bold">Manage filters</h2>
            <p className="text-gray-500 text-xs mt-0.5">
              Used by the portfolio directory and the project editor.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-900"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {error && (
          <div className="mx-5 mt-4 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-8">
          {loading ? (
            <p className="text-gray-500 text-sm">Loading…</p>
          ) : (
            AXES.map((axis) => {
              const items = rows.filter((r) => r.kind === axis.kind);
              return (
                <section key={axis.kind}>
                  <h3 className="text-gray-900 text-sm font-semibold mb-1">{axis.label}</h3>
                  <p className="text-gray-400 text-xs mb-3">{axis.hint}</p>

                  <div className="space-y-1.5 mb-3">
                    {items.length === 0 && (
                      <p className="text-gray-400 text-xs">None yet.</p>
                    )}

                    {items.map((row) => (
                      <div
                        key={row.id}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200"
                        data-testid={`filter-row-${row.id}`}
                      >
                        {editingId === row.id ? (
                          <>
                            <input
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') rename(row);
                                if (e.key === 'Escape') setEditingId(null);
                              }}
                              autoFocus
                              className="flex-1 px-2 py-1 rounded border border-gray-300 text-sm text-gray-900 focus:border-[#FF6B00] outline-none"
                            />
                            <button
                              type="button"
                              onClick={() => rename(row)}
                              aria-label="Save"
                              className="text-[#FF6B00] hover:opacity-80"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="flex-1 text-gray-800 text-sm">{row.name}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingId(row.id);
                                setEditValue(row.name);
                              }}
                              aria-label={`Rename ${row.name}`}
                              className="text-gray-400 hover:text-gray-900"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => remove(row)}
                              aria-label={`Delete ${row.name}`}
                              className="text-gray-400 hover:text-red-600"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <input
                      value={drafts[axis.kind]}
                      onChange={(e) =>
                        setDrafts((d) => ({ ...d, [axis.kind]: e.target.value }))
                      }
                      onKeyDown={(e) => e.key === 'Enter' && add(axis.kind)}
                      placeholder={`Add ${axis.label.toLowerCase().replace(/s$/, '')}…`}
                      aria-label={`New ${axis.label}`}
                      data-testid={`filter-new-${axis.kind}`}
                      className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:border-[#FF6B00] outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => add(axis.kind)}
                      disabled={busy || !drafts[axis.kind].trim()}
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#ff8124] disabled:opacity-40 text-[#1a1c1b] text-sm font-semibold"
                      data-testid={`filter-add-${axis.kind}`}
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Add
                    </button>
                  </div>
                </section>
              );
            })
          )}
        </div>
      </aside>
    </>
  );
};

export default FilterDrawer;
